import { useId, useState } from 'react';
import {
  PAYMENT_METHOD_LABEL,
  RECEIVED_METHODS,
  creditAddRefusal,
  creditRemoveRefusal,
  type ReceivedMethod,
} from '@/domain';
import { useAddCredit, useRemoveCredit } from '@/application/usecases/useFinance';
import { formatAmount } from '@/shared/format/money';
import { strings } from '@/shared/strings';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { modalStyles } from '@/ui/primitives/Modal';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { toast } from '@/ui/primitives/toastStore';
import { formRefusal } from '../clients/clientRefusal';
import { useConfirmAction } from '../shell/useConfirmAction';
import { parseAmount } from './moneyForm';
import styles from './Finance.module.css';

const t = strings.finance;

const FIELDS = ['amount', 'note', 'method', 'reference'] as const;
type Field = (typeof FIELDS)[number];

export interface CreditSheetProps {
  doctorId: string;
  /** What the doctor is called in the heading and the question: the name, or the ID for a reader without names. */
  doctorName: string;
  /** The credit the doctor has now. */
  balance: number;
  /** Adding to it, or taking off it. */
  mode: 'add' | 'remove';
  onClose: () => void;
}

/**
 * A doctor's credit with the lab, added to or taken off. The request of
 * 8 October 2026 (fourth).
 *
 * Adding is an advance, a refund or goodwill: an amount, what it is for, and
 * how the money came when money came. Taking off is paying it back, or
 * correcting an entry made by mistake: an amount no more than the doctor has,
 * and why. What it is for is required both ways, because it is the only thing
 * the doctor's page has to explain the figure with. Asked about first.
 */
export function CreditSheet({ doctorId, doctorName, balance, mode, onClose }: CreditSheetProps) {
  const add = useAddCredit();
  const remove = useRemoveCredit();
  const ask = useConfirmAction();
  const ids = { amount: useId(), note: useId(), method: useId(), reference: useId() };

  const adding = mode === 'add';
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [method, setMethod] = useState<ReceivedMethod | ''>('');
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [message, setMessage] = useState<string | null>(null);

  const pending = add.isPending || remove.isPending;

  function clear(field: Field) {
    setMessage(null);
    setErrors((current) => ({ ...current, [field]: undefined }));
  }

  function refused(failure: unknown): void {
    const refusal = formRefusal(failure, FIELDS);
    if (refusal === null) throw failure;
    setErrors(refusal.fields);
    setMessage(refusal.message);
  }

  function submit() {
    const sum = parseAmount(amount);
    const wrong = adding
      ? creditAddRefusal({ amount: sum, note, method: method === '' ? null : method, reference })
      : creditRemoveRefusal({ amount: sum, note }, balance);
    setMessage(null);
    if (wrong !== null) {
      setErrors({ [wrong.field]: wrong.message });
      return;
    }
    setErrors({});

    const figure = sum as number;
    if (adding) {
      ask(
        strings.confirm.addCredit(doctorName, formatAmount(figure)),
        () =>
          add
            .mutateAsync({
              doctorId,
              input: {
                amount: figure,
                note: note.trim(),
                method: method === '' ? null : method,
                reference: reference.trim() === '' ? null : reference.trim(),
              },
            })
            .then(() => {
              toast(t.creditAdded(formatAmount(figure)), { tone: 'success' });
              onClose();
            }, refused),
        { title: t.addCreditTitle(doctorName), confirmLabel: t.addCredit },
      );
      return;
    }

    ask(
      strings.confirm.removeCredit(doctorName, formatAmount(figure)),
      () =>
        remove.mutateAsync({ doctorId, input: { amount: figure, note: note.trim() } }).then(() => {
          toast(t.creditRemoved(formatAmount(figure)), { tone: 'success' });
          onClose();
        }, refused),
      { title: t.removeCreditTitle(doctorName), confirmLabel: t.removeCredit },
    );
  }

  return (
    <BottomSheet
      title={adding ? t.addCreditTitle(doctorName) : t.removeCreditTitle(doctorName)}
      lead={adding ? t.addCreditLead : t.removeCreditLead(formatAmount(balance))}
      closeLabel={strings.sheet.close}
      onClose={onClose}
      unsaved={
        (amount !== '' || note !== '') && !pending
          ? { question: t.discard, keep: strings.sheet.keep, discard: strings.sheet.discard }
          : null
      }
      footer={
        <>
          {message !== null && (
            <p className={styles.message} role="alert">
              {message}
            </p>
          )}
          <div className={sheetStyles.actions}>
            <Button onClick={onClose} disabled={pending}>
              {t.cancel}
            </Button>
            <Button variant="primary" className={sheetStyles.primary} loading={pending} onClick={submit}>
              {adding ? t.addCredit : t.removeCredit}
            </Button>
          </div>
        </>
      }
    >
      <div className={styles.form}>
        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.amount}>
            {t.amount}
          </label>
          <input
            id={ids.amount}
            className={modalStyles.input}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={amount}
            aria-required="true"
            aria-invalid={errors.amount !== undefined || undefined}
            aria-describedby={errors.amount === undefined ? undefined : `${ids.amount}-error`}
            data-autofocus
            onChange={(event) => {
              setAmount(event.target.value);
              clear('amount');
            }}
          />
          {errors.amount !== undefined && (
            <p id={`${ids.amount}-error`} className={modalStyles.error}>
              {errors.amount}
            </p>
          )}
        </div>

        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.note}>
            {t.creditNote}
          </label>
          <input
            id={ids.note}
            className={modalStyles.input}
            value={note}
            autoComplete="off"
            placeholder={adding ? t.creditNotePlaceholder : undefined}
            aria-required="true"
            aria-invalid={errors.note !== undefined || undefined}
            aria-describedby={errors.note === undefined ? undefined : `${ids.note}-error`}
            onChange={(event) => {
              setNote(event.target.value);
              clear('note');
            }}
          />
          {errors.note !== undefined && (
            <p id={`${ids.note}-error`} className={modalStyles.error}>
              {errors.note}
            </p>
          )}
        </div>

        {adding && (
          <div className={styles.pair}>
            <div className={modalStyles.field}>
              <label className={modalStyles.label} htmlFor={ids.method}>
                {t.method}
              </label>
              <select
                id={ids.method}
                className={modalStyles.select}
                value={method}
                onChange={(event) => {
                  setMethod(event.target.value as ReceivedMethod | '');
                  clear('method');
                }}
              >
                <option value="">{t.methodNone}</option>
                {RECEIVED_METHODS.map((key) => (
                  <option key={key} value={key}>
                    {PAYMENT_METHOD_LABEL[key]}
                  </option>
                ))}
              </select>
            </div>

            <div className={modalStyles.field}>
              <label className={modalStyles.label} htmlFor={ids.reference}>
                {t.reference}
              </label>
              <input
                id={ids.reference}
                className={modalStyles.input}
                value={reference}
                autoComplete="off"
                placeholder={t.referencePlaceholder}
                aria-invalid={errors.reference !== undefined || undefined}
                aria-describedby={errors.reference === undefined ? undefined : `${ids.reference}-error`}
                onChange={(event) => {
                  setReference(event.target.value);
                  clear('reference');
                }}
              />
              {errors.reference !== undefined && (
                <p id={`${ids.reference}-error`} className={modalStyles.error}>
                  {errors.reference}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
